<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { resolveSiteFileDownloadUrl } from "#bb/lib/pathContract";
import {
  adaptLegacyJwVideoSource,
  buildCloudflareStreamEmbedUrl,
  buildJwPlayerEmbedUrl,
  isApplicationFileVideoSource,
  isCloudflareStreamVideoSource,
  isJwPlayerVideoSource,
  isVideoSource,
} from "#bb/lib/video";
import type { VideoSource } from "#bb/types/blocks/content/VideoOption";
import { cn } from "@/lib/utils";

const props = defineProps<{ id: string }>();
const config = useRuntimeConfig();
const { siteId } = useSite();
const features = inject<{
  editor?: boolean;
  resourceDefinitionPath?: string | null;
}>("render-features", { editor: false });
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Video editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the video settings",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Video",
    icon: "lucide:video",
  },
});

const source = computed<VideoSource | null>(() => {
  if (isVideoSource(definitionData.value.source)) {
    return definitionData.value.source;
  }
  return adaptLegacyJwVideoSource(definitionData.value);
});

const playbackOptions = computed(() => ({
  autoplay: definitionData.value.autoplay === true,
  controls: definitionData.value.controls !== false,
  loop: definitionData.value.loop === true,
  muted: definitionData.value.muted === true,
  startSeconds: definitionData.value.startSeconds,
}));

const iframeUrl = computed(() => {
  if (source.value && isCloudflareStreamVideoSource(source.value)) {
    return buildCloudflareStreamEmbedUrl(source.value, playbackOptions.value);
  }
  if (source.value && isJwPlayerVideoSource(source.value)) {
    return buildJwPlayerEmbedUrl(source.value);
  }
  return "";
});

const fileUrl = computed(() => {
  if (!source.value || !isApplicationFileVideoSource(source.value)) return "";
  return resolveSiteFileDownloadUrl(
    source.value.path,
    config.public.apiPublicUrl || config.public.apiUrl,
    siteId.value,
    features.resourceDefinitionPath,
  );
});

const accessibleTitle = computed(() => {
  const title = definitionData.value.title;
  return typeof title === "string" && title.trim() ? title.trim() : "Video";
});
const configuredWidth = computed(() => {
  const width = Number(definitionData.value.width);
  return Number.isFinite(width) && width > 0 ? Math.round(width) : null;
});
const configuredHeight = computed(() => {
  const height = Number(definitionData.value.height);
  return Number.isFinite(height) && height > 0 ? Math.round(height) : null;
});
const containerStyle = computed(() => ({
  width: configuredWidth.value ? `${configuredWidth.value}px` : undefined,
  maxWidth: "100%",
}));
const mediaStyle = computed(() => ({
  height: configuredHeight.value ? `${configuredHeight.value}px` : undefined,
}));
</script>

<template>
  <div
    :class="cn(configuredWidth ? '' : 'w-full', $attrs.class ?? '')"
    :style="containerStyle"
  >
    <div
      v-if="iframeUrl"
      :class="[
        'w-full overflow-hidden rounded-lg bg-muted',
        configuredHeight ? '' : 'aspect-video',
      ]"
      :style="mediaStyle"
    >
      <iframe
        class="size-full border-0"
        :src="iframeUrl"
        :title="accessibleTitle"
        loading="lazy"
        allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
      />
    </div>

    <video
      v-else-if="fileUrl"
      :class="[
        'w-full rounded-lg bg-black object-contain',
        configuredHeight ? '' : 'aspect-video',
      ]"
      :style="mediaStyle"
      :src="fileUrl"
      :title="accessibleTitle"
      :autoplay="playbackOptions.autoplay"
      :controls="playbackOptions.controls"
      :loop="playbackOptions.loop"
      :muted="playbackOptions.autoplay || playbackOptions.muted"
      playsinline
      preload="metadata"
    />

    <div
      v-else
      :class="[
        'flex w-full items-center justify-center rounded-lg border border-dashed bg-muted/40 px-6 text-center',
        configuredHeight ? '' : 'aspect-video',
      ]"
      :style="mediaStyle"
      role="status"
    >
      <div class="space-y-2">
        <Icon name="lucide:video" class="mx-auto size-8 text-muted-foreground" />
        <p class="text-sm font-medium text-foreground">Video unavailable</p>
        <p class="text-xs text-muted-foreground">
          Choose an uploaded video in the block editor.
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  componentId: ComponentDefinitionIds.VIDEO,
  editor: () => import("#bb/components/editor/media/video.vue"),
  icon: "lucide:video",
  type: "Video",
  description: "Play an uploaded video using Cloudflare Stream when available.",
  default: {
    source: null,
    title: "Video",
    autoplay: false,
    controls: true,
    loop: false,
    muted: false,
    startSeconds: 0,
    width: 0,
    height: 0,
  },
  categories: ["Media"],
  order: 13053,
};
</script>
